/**
 * L'ISOLEMENT BIDIRECTIONNEL — écrit une fois, pour tout ce qui est une
 * STRUCTURE et non du texte.
 *
 * ── LE DÉFAUT, MESURÉ AU NAVIGATEUR ────────────────────────────────────────
 * 🔴 SOUS UN PARAGRAPHE RTL, UN MONTANT REND UNE DONNÉE FAUSSE :
 *
 *     12 345 678,90 €   s'affiche   € 678,90 345 12
 *      1 234,56 €       s'affiche   € 234,56 1
 *
 * **Les groupes de milliers sortent en ordre INVERSE**, et la cause est dans
 * notre propre code : `formatCurrency` sépare les milliers par une ESPACE.
 * Chaque groupe de chiffres devient une séquence bidi distincte, et les espaces
 * neutres entre elles se résolvent dans la direction du paragraphe — les
 * groupes se posent donc de droite à gauche.
 *
 * ⚠️ *Un séparateur de milliers invisible dans une langue est un OPÉRATEUR DE
 * RÉORDONNANCEMENT dans une autre.*
 *
 * 🔴 ET LE DÉFAUT SE CACHE — MAIS PAS COMME JE L'AI D'ABORD DIT. Un premier
 * rapport annonçait que `999,00 €` « s'affiche correctement ». **Remesuré sur
 * la chaîne EXACTE du produit** (espace U+0020 entre les milliers, U+00A0 avant
 * le symbole), c'est plus fin, et la distinction est celle qui compte :
 *   · **sous mille** — une seule séquence de chiffres, donc l'ORDRE DES CHIFFRES
 *     est juste ; seul le SYMBOLE passe de l'autre côté (`€ 999,00`). Mal placé.
 *   · **au-dessus de mille** — les groupes s'inversent. **Donnée FAUSSE.**
 * *Le défaut existait partout ; seule sa GRAVITÉ dépendait du montant* — et
 * c'est exactement ce qu'un jeu d'essai modeste ne montre jamais. Même famille
 * que le contraste qui n'apparaît qu'en mode sombre, et que la traduction figée
 * qui n'apparaît qu'avant le chargement du dictionnaire : *un défaut
 * conditionnel est un défaut que personne ne recette.*
 *
 * ── POURQUOI DES CARACTÈRES ET NON DU CSS ──────────────────────────────────
 * Quatre voies ont été MESURÉES au navigateur, sur la chaîne réelle du produit.
 *
 *   A. rien                                    → 🔴 faux
 *   B. espace insécable (U+00A0 / U+202F)      → chiffres remis en ordre,
 *                                                 mais le SYMBOLE change de côté
 *   C. `unicode-bidi: isolate` sur un parent   → 🔴 faux — **l'isolement d'un
 *      LOINTAIN                                   ancêtre isole l'ancêtre, il ne
 *                                                 fait rien de son contenu**
 *   D. `LRI … PDI` dans la CHAÎNE              → ✅ juste, et LTR inchangé
 *
 * 🔴 LA VOIE C ÉTAIT MA PROPOSITION, ET LA MESURE L'A INFIRMÉE. Elle avait
 * l'avantage de ne toucher aucune signature ; elle ne corrige simplement rien.
 * *Une voie élégante qu'on n'a pas mesurée est une voie qu'on ne connaît pas.*
 *
 * ⚠️ CE QUE LA VOIE D ÉVITE, ET C'EST TOUT L'ARGUMENT : `formatCurrency` garde
 * sa **signature** — elle rend une chaîne, comme le PDF (`pdf_service`) et les
 * trois sites qui l'interpolent dans un gabarit. Rendre du JSX aurait cassé ces
 * trois-là ; un composant `<Montant>` aurait laissé **38 sites à convertir**.
 * *Le correctif qui ne crée pas de dette est celui qui ne change pas de forme.*
 *
 * ⚠️ ET LTR EST RIGOUREUSEMENT INCHANGÉ — mesuré des deux côtés. *Un correctif
 * RTL qui modifierait le rendu français serait pire que le défaut qu'il
 * corrige*, parce qu'il déplacerait le risque vers la langue majoritaire.
 */

/**
 * U+2066 LEFT-TO-RIGHT ISOLATE — ouvre une séquence dont la direction interne
 * est GAUCHE-DROITE, et qui compte pour un caractère neutre unique vis-à-vis
 * de ce qui l'entoure.
 *
 * ⚠️ ÉCRITS EN ÉCHAPPEMENTS, JAMAIS EN CLAIR. Ces caractères sont INVISIBLES :
 * collés tels quels dans la source, ils seraient indiscernables d'une chaîne
 * vide, impossibles à repérer en relecture et faciles à supprimer par accident.
 */
export const LRI = '\u2066';

/** U+2069 POP DIRECTIONAL ISOLATE — referme l'isolement ouvert par `LRI`. */
export const PDI = '\u2069';

/**
 * Isole `texte` en gauche-droite pour l'AFFICHAGE.
 *
 * 🔴 POUR L'AFFICHAGE, ET POUR LUI SEUL. Le résultat porte deux caractères
 * invisibles : il ne doit **jamais** être stocké, envoyé au serveur, comparé,
 * découpé, ni servir de nom de fichier. *Une valeur d'affichage qui remonte
 * dans une donnée est une donnée qu'on ne pourra plus comparer à elle-même.*
 *
 * ⚠️ Une valeur vide reste vide : isoler du néant produirait deux caractères
 * invisibles là où l'écran doit ne rien montrer — et `Boolean('')` deviendrait
 * `true` chez tout appelant qui teste la chaîne avant de l'afficher.
 */
export const isolerLtr = (texte) => {
  const s = texte == null ? '' : String(texte);
  return s === '' ? '' : LRI + s + PDI;
};
